import {
  Center,
  Heading,
  HStack,
  Image,
  Pressable,
  Spacer,
  Spinner,
  Stack,
  Text,
  VStack,
} from "native-base";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import qs from "qs";
import { RiFolderWarningLine } from "react-icons/ri";
import styles from "./styles.module.css";
import colors from "../../../constants/colors";
import DatePickerCustomInput from "../../../components/datePickerCustomInput";
import Cell from "../../../components/cell";
import bus from "../../../assets/icons/bus.svg";
import location from "../../../assets/icons/location.svg";
import seat from "../../../assets/icons/seat.svg";
import calendar from "../../../assets/icons/calendar.svg";
import routes from "../../../routes";
import API from "../../../api";
import { StateContext, StatusContext } from "../../../contexts";

function AgencyTripsAndPassengersPage() {
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialDate, setInitialDate] = useState(
    searchParams.get("initialDate")
      ? moment(searchParams.get("initialDate")).toDate()
      : moment().startOf("day").toDate(),
  );
  const [finalDate, setFinalDate] = useState(
    searchParams.get("finalDate")
      ? moment(searchParams.get("finalDate")).toDate()
      : moment().add(1, "week").endOf("day").milliseconds(0).toDate(),
  );
  const [tours, setTours] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const getTours = useCallback(
    async (signal) => {
      try {
        setIsLoading(true);
        const query = qs.stringify({
          itinerary: {
            type: "origin",
            isReturn: false,
            departureDate: {
              $gte: initialDate.toISOString(),
              $lte: finalDate.toISOString(),
            },
          },
        });
        const {
          data: {
            data: _tours,
            meta: { total: _total },
          },
        } = await API(state.jwt).get(
          `/tours/agency?${query}`,
          signal ? { signal } : {},
        );
        setTotal(_total);
        setTours(_tours);
      } catch (error) {
        status.updateErrorMessage(error);
      }
      setIsLoading(false);
    },
    [initialDate, finalDate, state.jwt, status],
  );

  const updateSearchParams = useCallback(() => {
    navigate(
      {
        search: qs.stringify({
          initialDate: initialDate.toISOString(),
          finalDate: finalDate.toISOString(),
        }),
      },
      { replace: true },
    );
  }, [navigate, initialDate, finalDate]);

  useEffect(() => {
    updateSearchParams();
    const controller = new AbortController();
    getTours(controller.signal);
    return () => {
      controller.abort();
    };
  }, [getTours, updateSearchParams]);

  const onApplyFilters = () => {
    updateSearchParams();
    getTours();
  };

  const isFilterApplied = useMemo(
    () =>
      searchParams.get("initialDate") === initialDate.toISOString() &&
      searchParams.get("finalDate") === finalDate.toISOString(),
    [searchParams, initialDate, finalDate],
  );

  return (
    <VStack>
      <Helmet>
        <title>Viajes y pasajeros</title>
      </Helmet>
      <HStack paddingY={8}>
        <Heading color={colors.secondary} fontSize="2xl">
          Viajes y pasajeros
        </Heading>
      </HStack>
      <Text>Fecha de inicio de viaje</Text>
      <Spacer height={2} />
      <HStack space={5} alignItems="flex-end" zIndex={1}>
        <DatePicker
          selected={initialDate}
          onChange={setInitialDate}
          customInput={<DatePickerCustomInput label="Rango inicial" />}
          maxDate={finalDate}
          dateFormat="yyyy-MM-dd"
        />
        <DatePicker
          selected={finalDate}
          onChange={setFinalDate}
          customInput={<DatePickerCustomInput label="Rango inicial" />}
          minDate={initialDate}
          dateFormat="yyyy-MM-dd"
        />
        <Pressable
          backgroundColor={colors.primary}
          paddingX={4}
          paddingY={1}
          borderRadius={5}
          onPress={onApplyFilters}
          disabled={isFilterApplied}>
          <Text color={colors.white}>Aplicar</Text>
        </Pressable>
      </HStack>
      <Spacer height={2} />
      <Text fontSize="sm" textAlign="right">
        Se han encontrado {total} resultado{total !== 1 && "s"}
      </Text>
      <Spacer height={5} />
      <Stack overflowX="auto">
        {isLoading ? (
          <Center height={300}>
            <Spinner size="lg" color={colors.primary} />
          </Center>
        ) : tours.length ? (
          <Stack minWidth={2000}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <Cell header color={colors.white}>
                    Nombre del viaje
                  </Cell>
                  <Cell header color={colors.white}>
                    Folio
                  </Cell>
                  <Cell header color={colors.white}>
                    Estado
                  </Cell>
                  <Cell header color={colors.white}>
                    Origen
                  </Cell>
                  <Cell header color={colors.white}>
                    Destino
                  </Cell>
                  <Cell header color={colors.white}>
                    Asientos
                  </Cell>
                  <Cell header color={colors.white}>
                    Fecha
                  </Cell>
                  <Cell header color={colors.white}>
                    Acciones
                  </Cell>
                </tr>
              </thead>
              <tbody>
                {/* TODO: Create component */}
                {tours.map((tour) => {
                  const origin = tour.itinerary.find(
                    (location) =>
                      !location.isReturn && location.type === "origin",
                  );
                  const destination = tour.itinerary.find(
                    (location) =>
                      !location.isReturn && location.type === "destination",
                  );

                  return (
                    <tr key={String(tour.id)}>
                      <Cell bold>{tour?.name || destination?.name}</Cell>
                      <Cell>{tour.id}</Cell>
                      <Cell>
                        <Text>{tour.status ? "Confirmado" : "Pendiente"}</Text>
                      </Cell>
                      <Cell>
                        <HStack space={1}>
                          <Image src={bus} alt="bus" width={25} height={25} />
                          <Text flex={1}>{origin?.name}</Text>
                        </HStack>
                      </Cell>
                      <Cell>
                        <HStack space={1}>
                          <Image
                            src={location}
                            alt="location"
                            width={25}
                            height={25}
                          />
                          <Text flex={1}>{destination?.name}</Text>
                        </HStack>
                      </Cell>
                      <Cell>
                        <HStack space={1}>
                          <Image src={seat} alt="seat" width={25} height={25} />
                          <Text flex={1}>
                            {tour.services.reduce(
                              (total, service) =>
                                total + service.quotation.vehicleType.capacity,
                              0,
                            )}
                          </Text>
                        </HStack>
                      </Cell>
                      <Cell>
                        <HStack space={2}>
                          <Image
                            src={calendar}
                            alt="calendar"
                            width={25}
                            height={25}
                          />
                          <Text numberOfLines={1}>
                            {moment(origin?.departureDate).format(
                              moment.HTML5_FMT.DATE,
                            )}
                          </Text>
                        </HStack>
                      </Cell>
                      <Cell>
                        <HStack
                          flexWrap="wrap"
                          justifyContent="center"
                          style={{ rowGap: 5, columnGap: 5 }}>
                          <Link
                            to={routes.agencyPaymentManagementInstallments.replace(
                              ":id",
                              tour.id,
                            )}>
                            <Stack
                              backgroundColor={colors.primary}
                              borderRadius="full"
                              paddingY={3}
                              paddingX={6}>
                              <Text color={colors.white} lineHeight={16}>
                                Abonos
                              </Text>
                            </Stack>
                          </Link>
                          <Link
                            to={routes.agencyPaymentManagementServices.replace(
                              ":id",
                              tour.id,
                            )}>
                            <Stack
                              backgroundColor={colors.primary}
                              borderRadius="full"
                              paddingY={3}
                              paddingX={6}>
                              <Text
                                color={colors.white}
                                textAlign="center"
                                lineHeight={16}>
                                Ver servicios
                              </Text>
                            </Stack>
                          </Link>
                          <Link
                            to={routes.agencyPaymentManagementPricing.replace(
                              ":id",
                              tour.id,
                            )}>
                            <Stack
                              backgroundColor={colors.primary}
                              borderRadius="full"
                              paddingY={3}
                              paddingX={6}>
                              <Text
                                color={colors.white}
                                textAlign="center"
                                lineHeight={16}>
                                Asignación de precios
                              </Text>
                            </Stack>
                          </Link>
                        </HStack>
                      </Cell>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Stack>
        ) : (
          <Center height={300}>
            <RiFolderWarningLine color={colors.secondary} fontSize={100} />
            <Text color={colors.secondary}>No hay información disponible</Text>
          </Center>
        )}
      </Stack>
    </VStack>
  );
}

export default AgencyTripsAndPassengersPage;
